import { useState } from "react"
import { Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { copyTextToClipboard } from "../../../../../lib/clipboard"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { StandardButton } from "../../../buttons/StandardButton"
import { StandardDialog } from "../../../dialogs/StandardDialog"

const COPIED_FEEDBACK_MS = 2000

const useStyles = makeStyles()((theme) => ({
    content: {
        paddingBottom: "1rem",
    },
    list: {
        backgroundColor: theme.palette.tailwind.slate[50],
        borderRadius: ".5rem",
        padding: ".75rem 1rem",
    },
    date: {
        color: theme.palette.tailwind.slate[700],
    },
    actions: {
        marginTop: ".5rem",
    },
}))

type BusyResourceDatesDialogProps = Readonly<{
    open: boolean
    busyDates: ReadonlyArray<string>
    onContinue: () => void
}>

export function BusyResourceDatesDialog({ open, busyDates, onContinue }: BusyResourceDatesDialogProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const [copied, setCopied] = useState(false)

    const formattedDates = busyDates.map((date) => ({
        raw: date,
        label: dateTimeFactory.formatDate(dateTimeFactory.fromISO(date)),
    }))

    const handleCopy = async () => {
        const success = await copyTextToClipboard(formattedDates.map((date) => date.label).join("\n"))
        if (success) {
            setCopied(true)
            setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS)
        }
    }

    return (
        <StandardDialog
            open={open}
            onClose={onContinue}
            maxWidthProp="xs"
            disableBackdropClick
            hideCloseIcon
            dialogTitle={
                <LocalizedStrict id="create-order-busy-resource-dates-dialog-title">
                    <span>Some dates could not be booked</span>
                </LocalizedStrict>
            }
        >
            <Stack gap="1rem" className={classes.content}>
                <LocalizedStrict id="create-order-busy-resource-dates-dialog-message">
                    <Typography variant="body1">
                        The selected resources were already booked on the following dates, so those occurrences were not
                        booked:
                    </Typography>
                </LocalizedStrict>
                <Stack gap=".25rem" className={classes.list}>
                    {formattedDates.map((date) => (
                        <Typography key={date.raw} variant="body2" className={classes.date}>
                            {date.label}
                        </Typography>
                    ))}
                </Stack>
                <Stack direction="row" gap="1rem" justifyContent="flex-end" className={classes.actions}>
                    <LocalizedStrict
                        id={
                            copied
                                ? "create-order-busy-resource-dates-dialog-copied-button"
                                : "create-order-busy-resource-dates-dialog-copy-button"
                        }
                    >
                        <StandardButton
                            type="button"
                            variant="outlined"
                            fullWidth={false}
                            onClick={() => void handleCopy()}
                            startIcon={<FontAwesomeIcon icon={faCopy} />}
                        >
                            {copied ? "Copied" : "Copy list"}
                        </StandardButton>
                    </LocalizedStrict>
                    <LocalizedStrict id="create-order-busy-resource-dates-dialog-continue-button">
                        <StandardButton type="button" color="blue" fullWidth={false} onClick={onContinue}>
                            Continue
                        </StandardButton>
                    </LocalizedStrict>
                </Stack>
            </Stack>
        </StandardDialog>
    )
}
